import type { MapBoundsConfig } from '@/components/map/map.types';
import { FileInfo, StoredFile } from './common.interface';
import { LanguageTranslation } from './language.interface';

export interface DTOModule {
  id?: number;
  title: LanguageTranslation;
  project_id: number;
  block_type: number;
  config: ProjectModuleConfigDto;
}

export interface Module {
  id?: number;
  title: LanguageTranslation;
  project_id: number;
  block_type: number;
  config: ProjectModuleConfigDto;
}

export interface ProjectModuleConfigDto {
  highlight: boolean;
  includeInTableOfContents: boolean;
  ctaButtons?: {
    button1: ProjectModuleConfigCTAButtonDto;
    button2: ProjectModuleConfigCTAButtonDto;
  };
}

export interface ProjectModuleTextConfigDTO extends ProjectModuleConfigDto {
  content: LanguageTranslation;
}

export interface ProjectModuleImageTextConfigDto extends ProjectModuleTextConfigDTO {
  hasImage: boolean;
  image: FileInfo | null;
  imageAlignment: number;
  imageCaption: LanguageTranslation;
}

export interface ProjectModuleVideoConfigDto extends ProjectModuleTextConfigDTO {
  videoType: string;
  video: LanguageTranslation;
  videoAlignment: number;
  videoCaption: LanguageTranslation;
}

export interface ProjectModuleConsultationConfigDto extends ProjectModuleConfigDto {
  consultationId: number;
  slug: string;
  useConsultationBanner: boolean;
  image: FileInfo | null;
}

export interface ProjectModuleSubscriptionConfigDto extends ProjectModuleConfigDto {
  isUserSubscribed: boolean;
  subscriberCount: number;
}

export interface ProjectModuleConfigCTAButtonDto {
  enabled: boolean;
  text: LanguageTranslation;
  linkType: 'url' | 'module';
  url?: LanguageTranslation;
  moduleId?: number;
}

export interface ProjectModuleHeroConfigDto extends ProjectModuleConfigDto {
  useProjectTitle?: boolean;
  useProjectImage?: boolean;
  displayMode: 'image' | 'video';
  image: FileInfo | null;
  videoType: string;
  video: LanguageTranslation;
  videoCaption: LanguageTranslation;
  overlay: ProjectModuleHeroOverlayConfigDto;
  ctaButtons: {
    button1: ProjectModuleConfigCTAButtonDto;
    button2: ProjectModuleConfigCTAButtonDto;
  };
  layout?: number;
}

export interface ProjectModuleHeroOverlayConfigDto {
  backgroundColor: string;
  backgroundOpacity: number;
  textColor: string;
  description: LanguageTranslation;
  alignment: number;
}

export interface ProjectModuleEventConfigDto extends ProjectModuleConfigDto {
  content: LanguageTranslation;
}

export interface ProjectModuleFeedbackConfigDto extends ProjectModuleConfigDto {
  description: LanguageTranslation;
  thankYouMessage: LanguageTranslation;
  maxLength: number | null;
  anonymousFeedback?: boolean;
}

export interface ProjectModuleQuestionAnswerConfigDto extends ProjectModuleConfigDto {
  description: LanguageTranslation;
  thankYouMessage: LanguageTranslation;
  noAnswersMessage: LanguageTranslation;
  maxLength: number | null;
}

export interface ProjectModuleDocumentSubmissionConfigDto extends ProjectModuleConfigDto {
  description?: LanguageTranslation;
  submitButtonLabel: LanguageTranslation;
  thankYouMessage: LanguageTranslation;
}

export interface ProjectModuleMapLayerDto {
  id: number;
  name: LanguageTranslation | null;
  file: {
    url?: string | null;
    mimetype?: string | null;
    size?: number | null;
    name?: string | null;
  } | null;
  bbox?: number[] | null;
  defaultVisibility?: boolean;
}

export interface ProjectModuleMapConfigDto extends ProjectModuleConfigDto {
  description: LanguageTranslation;
  mapStyle: string | null;
  bounds: MapBoundsConfig | null;
  zoom: number | null;
  layers?: ProjectModuleMapLayerDto[] | null;
  hideAddressSearch?: boolean;
}

export interface ProjectModuleEventItemConfigDto {
  id?: number;
  project_module_id: number;
  title: LanguageTranslation;
  description: LanguageTranslation;
  begin_date: string;
  end_date?: string | null; // Optional and can be null
  start_time?: string;
  end_time?: string;
  location?: LanguageTranslation;
  config: ProjectModuleEventItemConfigConfigDto;
  status: number;
}

export interface ProjectModuleEventItemConfigConfigDto {
  image: FileInfo | StoredFile | null;
  ctaButtons: {
    button1: ProjectModuleConfigCTAButtonDto;
  };
}

export interface ProjectModuleNewsConfigDto extends ProjectModuleConfigDto {
  content: LanguageTranslation;
}

export interface ProjectModuleNewsItemConfigDto {
  id?: number;
  project_module_id: number;
  title: LanguageTranslation;
  description: LanguageTranslation;
  date: string;
  config: ProjectModuleNewsItemConfigConfigDto;
  status: number;
}

export interface ProjectModuleNewsItemConfigConfigDto {
  image: StoredFile | null;
  content?: LanguageTranslation;
}

export function parseModule(dto: DTOModule): Module {
  return {
    id: dto.id,
    title: dto.title,
    project_id: dto.project_id,
    block_type: dto.block_type,
    config: dto.config,
  } as Module;
}
